import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { Button, ConfirmDialog, Dialog } from "@activecollab/components";
import { Formik } from "formik";
import isEqual from "lodash/isEqual";
import moment from "moment";
import PropTypes from "prop-types";
import { showErrorToast } from "~/components/Toast";
import { useFetchSelectedJobTypesForUser, useUpdateAvailableJobTypesForUser } from "~/repository";
import { getConfigOptions } from "~/store/actions/actions.configOptions";
import {
  addUserInternalRate,
  deleteUserInternalRate,
  fetchAllInternalRates,
} from "~/store/actions/actions.userInternalRates";
import { editUserWorkweek, fetchUserWorkweek } from "~/store/actions/actions.userWorkweeks";
import { getDefaultCurrency } from "~/store/selectors/selector.currencies";
import { getRatesByUser, makeGetCurrentUserInternalRate } from "~/store/selectors/selector.userInternalRates";
import { getUserById } from "~/store/selectors/selector.users";
import { getUserWorkweekById } from "~/store/selectors/selector.weeklyWorkerCapacity";
import { validateWorkWeekHours } from "~/utils/utils";
import { AddHourlyRateDialog } from "./AddHourlyRateDialog";
import UserRatesAndCapacityForm from "./UserRatesAndCapacityForm";
import { useGlobalPrivateChannelListener } from "../Hooks/useGlobalPrivateChannelListener";

export const UserRatesAndCapacityDialog = ({ userId, isOpen, close, workWeekData }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddHourlyRateDialog, setOpenAddHourlyRateDialog] = useState(false);
  const [selectedHourlyRateId, setSelectedHourlyRateId] = useState(null);

  const workWeekDataFromComponent = useSelector((state) => getUserWorkweekById(state, userId));

  useEffect(() => {
    if (!workWeekData) {
      dispatch(fetchUserWorkweek(userId));
    }
  }, [dispatch, userId, workWeekData]);

  useEffect(() => {
    dispatch(getConfigOptions(["time_first_week_day", "time_workdays", "weekly_worker_capacity"]));
    dispatch(fetchAllInternalRates());
  }, [dispatch, userId]);

  const resolvedWorkWeekData = useMemo(() => {
    return workWeekData ?? workWeekDataFromComponent;
  }, [workWeekData, workWeekDataFromComponent]);

  const getCurrentUserInternalRate = useMemo(() => makeGetCurrentUserInternalRate(), []);
  const currentUserInternalRate = useSelector((state) => getCurrentUserInternalRate(state, userId));

  const user = useSelector((state) => getUserById(state, userId));

  const allUserHourlyRates = useSelector((state) => getRatesByUser(state, userId));

  const defaultCurrency = useSelector(getDefaultCurrency);

  const { data: selectedJobTypes } = useFetchSelectedJobTypesForUser(userId, isOpen, true);

  const { mutateAsync: updateJobTypes } = useUpdateAvailableJobTypesForUser();

  const initialFormValues = useMemo(() => {
    return {
      weeklyWorkerCapacity: resolvedWorkWeekData?.capacity,
      selectedJobTypes: selectedJobTypes
        ? selectedJobTypes.reduce((acc, id) => {
            return { ...acc, [id]: true };
          }, {})
        : {},
      internalHourlyRate: currentUserInternalRate ? currentUserInternalRate : 0,
      capacity_source: resolvedWorkWeekData?.capacity_source,
    };
  }, [
    currentUserInternalRate,
    resolvedWorkWeekData?.capacity,
    resolvedWorkWeekData?.capacity_source,
    selectedJobTypes,
  ]);

  const handleSubmit = useCallback(
    (formValues, setSubmitting) => {
      const mutations = [];

      if (!isEqual(initialFormValues.weeklyWorkerCapacity, formValues.weeklyWorkerCapacity)) {
        mutations.push(
          dispatch(editUserWorkweek(userId, formValues.weeklyWorkerCapacity)).then((res) => {
            if (res.type === "EDIT_USER_WORKWEEK_ERROR") {
              throw new Error();
            }
          })
        );
      }

      if (!isEqual(initialFormValues.selectedJobTypes, formValues.selectedJobTypes)) {
        mutations.push(
          updateJobTypes({
            job_type_ids: Object.keys(formValues.selectedJobTypes).map((id) => +id),
            userId: userId,
          }).catch(() => {
            showErrorToast(t("There was a problem in updating rates and capacity."));
            throw new Error();
          })
        );
      }

      if (initialFormValues.internalHourlyRate !== formValues.internalHourlyRate) {
        const attr = {
          valid_from: moment().format("YYYY-MM-DD"),
          rate: formValues.internalHourlyRate,
          user_id: userId,
          user_name: user.display_name,
          user_email: user.email,
        };
        mutations.push(
          dispatch(addUserInternalRate(userId, attr)).then((res) => {
            if (res.type === "ADD_USER_INTERNAL_RATE_ERROR") {
              throw new Error();
            }
          })
        );
      }

      Promise.all(mutations)
        .then(() => {
          queryClient.invalidateQueries("available-job-types");
          return close();
        })
        .catch(() => {})
        .finally(() => {
          return setSubmitting(false);
        });
    },
    [
      close,
      dispatch,
      initialFormValues.internalHourlyRate,
      initialFormValues.selectedJobTypes,
      initialFormValues.weeklyWorkerCapacity,
      queryClient,
      t,
      updateJobTypes,
      user?.display_name,
      user?.email,
      userId,
    ]
  );

  const handleCancelDeleteClick = useCallback(() => {
    setOpenDeleteDialog(false);
  }, []);

  const handleDeleteClick = useCallback(() => {
    dispatch(deleteUserInternalRate(selectedHourlyRateId));
    setOpenDeleteDialog(false);
  }, [dispatch, selectedHourlyRateId]);

  const handleCancelAddHourlyRateDialog = useCallback(() => {
    setOpenAddHourlyRateDialog(false);
  }, []);

  const handleOnAddHourlyRate = useCallback(
    (attributes) => {
      const modifiedAttributes = {
        ...attributes,
        user_id: userId,
        user_name: user?.display_name,
        user_email: user?.email,
      };
      dispatch(addUserInternalRate(userId, modifiedAttributes));
      setOpenAddHourlyRateDialog(false);
    },
    [dispatch, user?.display_name, user?.email, userId]
  );

  const availableJobTypeslistener = useCallback(() => {
    queryClient.invalidateQueries(["selected-job-types-for-user", userId]);
  }, [queryClient, userId]);

  useGlobalPrivateChannelListener("UserAvailableJobTypesUpdated", availableJobTypeslistener);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={close}
      >
        <Formik
          initialValues={initialFormValues}
          enableReinitialize
          onSubmit={(values, { setSubmitting }) => {
            handleSubmit(values, setSubmitting);
          }}
          validate={(values) => {
            const errors = {};
            values.weeklyWorkerCapacity?.some((capacity) => {
              if (!validateWorkWeekHours(capacity ? capacity / 60 : capacity)) {
                errors.weeklyWorkerCapacity = true;
              }
            });
            if (values.weeklyWorkerCapacity?.every((capacity) => capacity === 0)) {
              errors.weeklyWorkerCapacity = true;
            }

            return errors;
          }}
        >
          {({ setFieldValue, dirty, isValid, values, submitForm, isSubmitting }) => {
            return (
              <>
                <Dialog.Title>{t("Change Rates and Capacity")}</Dialog.Title>
                <Dialog.ContentDivider />
                <Dialog.Content>
                  <UserRatesAndCapacityForm
                    allUserHourlyRates={allUserHourlyRates}
                    defaultCurrency={defaultCurrency}
                    openDeleteDialog={openDeleteDialog}
                    setOpenDeleteDialog={setOpenDeleteDialog}
                    setFieldValue={setFieldValue}
                    setOpenAddHourlyRateDialog={setOpenAddHourlyRateDialog}
                    setSelectedHourlyRateId={setSelectedHourlyRateId}
                    workWeekData={resolvedWorkWeekData}
                    values={values}
                  />
                </Dialog.Content>
                <Dialog.ContentDivider />
                <Dialog.Actions>
                  <Button
                    type="button"
                    variant="primary"
                    className="tw-mr-3"
                    onClick={() => submitForm()}
                    disabled={!dirty || !isValid || isSubmitting}
                  >
                    {t("Save")}
                  </Button>
                  <Button
                    type="button"
                    onClick={close}
                    variant="secondary"
                    disabled={isSubmitting}
                  >
                    {t("Cancel")}
                  </Button>
                </Dialog.Actions>
              </>
            );
          }}
        </Formik>
      </Dialog>
      {openAddHourlyRateDialog ? (
        <AddHourlyRateDialog
          onCancel={handleCancelAddHourlyRateDialog}
          onAddHourlyRate={handleOnAddHourlyRate}
          userId={userId}
          open={openAddHourlyRateDialog}
          defaultCurrencyCode={defaultCurrency?.code}
          allUserHourlyRates={allUserHourlyRates}
        />
      ) : null}
      {openDeleteDialog ? (
        <ConfirmDialog
          open={openDeleteDialog}
          dialogTitle={t("Delete Hourly Rate")}
          dialogContent={t("Deleting hourly rates may affect the Profitability report.")}
          onCancel={handleCancelDeleteClick}
          onConfirm={handleDeleteClick}
          confirmBtnText={t("Delete")}
        />
      ) : null}
    </>
  );
};

UserRatesAndCapacityDialog.propTypes = {
  userId: PropTypes.number.isRequired,
  isOpen: PropTypes.bool,
  close: PropTypes.func,
  workWeekData: PropTypes.object,
};

UserRatesAndCapacityDialog.displayName = "UserRatesAndCapacityDialog";
